/**
 * Premium TTS (Text-to-Speech) Hook for Little Genius Hub
 * Uses OpenAI TTS via backend API for high-quality narration
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available premium voices
export const PREMIUM_VOICES = [
  { id: 'nova', name: 'Nova', description: 'Energetic, upbeat (recommended for kids)' },
  { id: 'shimmer', name: 'Shimmer', description: 'Bright, cheerful' },
  { id: 'fable', name: 'Fable', description: 'Expressive, storytelling' },
  { id: 'echo', name: 'Echo', description: 'Smooth, calm' },
  { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced' },
  { id: 'onyx', name: 'Onyx', description: 'Deep, authoritative' }
];

export function usePremiumTTS() {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [usePremium, setUsePremium] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const audioRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Stop any currently playing audio
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    // Also stop browser TTS
    window.speechSynthesis.cancel();
    setIsPlaying(false);
  }, []);

  // Fallback to browser TTS
  const speakWithBrowserTTS = useCallback((text) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Try to get a good voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoices = ['Samantha', 'Google US English', 'Karen', 'Victoria'];
    for (const preferred of preferredVoices) {
      const voice = voices.find(v => v.name.includes(preferred));
      if (voice) {
        utterance.voice = voice;
        break;
      }
    }
    
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);
    
    window.speechSynthesis.speak(utterance);
  }, []);

  // Speak text using premium TTS
  const speak = useCallback(async (text, options = {}) => {
    const voice = options.voice || selectedVoice;
    const speed = options.speed || 1.0;
    const fallbackToBrowser = options.fallbackToBrowser !== false;

    // Stop any current audio
    stop();
    setError(null);

    // If premium is disabled, use browser TTS
    if (!usePremium) {
      speakWithBrowserTTS(text);
      return;
    }

    setIsLoading(true);

    try {
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Request TTS generation
      const response = await axios.post(
        `${API}/tts/generate`,
        { text, voice, speed },
        { signal: abortControllerRef.current.signal }
      );

      const { audio_key } = response.data;

      // Create audio element and play
      const audio = new Audio(`${API}/tts/audio/${audio_key}.mp3`);
      audioRef.current = audio;

      audio.onloadeddata = () => {
        setIsLoading(false);
      };

      audio.onplay = () => {
        setIsPlaying(true);
      };

      audio.onended = () => {
        setIsPlaying(false);
      };

      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        setIsLoading(false);
        setIsPlaying(false);
        setError('Failed to play audio');
        
        if (fallbackToBrowser) {
          speakWithBrowserTTS(text);
        }
      };

      await audio.play();

    } catch (err) {
      console.error('TTS error:', err);
      setIsLoading(false);
      setError(err.message || 'TTS generation failed');

      // Fallback to browser TTS
      if (fallbackToBrowser) {
        speakWithBrowserTTS(text);
      }
    }
  }, [selectedVoice, usePremium, stop, speakWithBrowserTTS]);

  // Toggle between premium and browser TTS
  const togglePremium = useCallback(() => {
    setUsePremium(prev => !prev);
  }, []);

  return {
    speak,
    stop,
    isLoading,
    isPlaying,
    error,
    usePremium,
    setUsePremium,
    togglePremium,
    selectedVoice,
    setSelectedVoice,
    voices: PREMIUM_VOICES
  };
}

export default usePremiumTTS;
